import React from "react";
import { motion } from "framer-motion";
import { useI18n } from "../i18n/I18nContext";
import SectionHead from "../components/SectionHead";

const ease = [0.16, 1, 0.3, 1];

// SlowFadeGraph — illustrative life-decades curve. Labels are localized.
function SlowFadeGraph({ labelYour, labelNow }) {
  const decades = ["20", "30", "40", "50", "60", "70"];
  return (
    <svg
      viewBox="0 0 1000 360"
      className="slowfade-svg"
      role="img"
      aria-label="the slow fade: illustrative decades curve"
      data-testid="slow-fade-graph"
    >
      <line x1="40" y1="320" x2="960" y2="320" stroke="currentColor" strokeWidth="0.8" opacity="0.5" />
      {[0.25, 0.5, 0.75].map((h, i) => (
        <line
          key={i}
          x1="40"
          y1={320 - h * 220}
          x2="960"
          y2={320 - h * 220}
          stroke="currentColor"
          strokeWidth="0.4"
          opacity="0.08"
          strokeDasharray="2 4"
        />
      ))}
      <path
        d="M 50 110 C 230 118, 380 145, 520 185 S 800 245, 950 280"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        opacity="0.92"
      />
      <circle cx="280" cy="135" r="3" fill="currentColor" />
      <line x1="280" y1="135" x2="280" y2="335" stroke="currentColor" strokeWidth="0.4" opacity="0.3" strokeDasharray="2 3" />
      {decades.map((d, i) => {
        const x = 50 + (i / (decades.length - 1)) * 900;
        return (
          <g key={d}>
            <line x1={x} y1="320" x2={x} y2="328" stroke="currentColor" strokeWidth="0.8" />
            <text
              x={x}
              y="348"
              textAnchor="middle"
              fontFamily="JetBrains Mono, monospace"
              fontSize="12"
              letterSpacing="2"
              fill="currentColor"
              opacity="0.55"
            >
              {d}
            </text>
          </g>
        );
      })}
      <text
        x="60"
        y="100"
        fontFamily="JetBrains Mono, monospace"
        fontSize="10.5"
        letterSpacing="2"
        fill="currentColor"
        opacity="0.55"
      >
        {labelYour}
      </text>
      <text
        x="288"
        y="125"
        fontFamily="JetBrains Mono, monospace"
        fontSize="10"
        letterSpacing="2"
        fill="currentColor"
        opacity="0.6"
      >
        {labelNow}
      </text>
    </svg>
  );
}

export default function WhyNow() {
  const { t } = useI18n();
  const w = t.whynow;
  return (
    <section
      id="whynow"
      className="section border-t hair"
      data-testid="whynow-section"
    >
      <div className="container-edge">
        <SectionHead
          roman={t.sections.whynow.roman}
          right={<span className="mono" style={{ color: "var(--faded)" }}>fig. 02</span>}
        />

        <div className="grid grid-cols-12 gap-y-10 md:gap-x-10 mb-24 md:mb-32">
          <motion.h2
            className="col-span-12 md:col-span-8 display text-[44px] sm:text-[60px] md:text-[80px] lg:text-[100px]"
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease }}
            data-testid="whynow-threat"
          >
            {w.threat}
          </motion.h2>

          <motion.p
            className="col-span-12 md:col-span-4 md:col-start-9 self-end"
            style={{ color: "var(--ink)", fontWeight: 300, fontSize: "18px", lineHeight: 1.4, maxWidth: "30ch" }}
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, ease, delay: 0.15 }}
            data-testid="whynow-hope"
          >
            {w.hope}
          </motion.p>
        </div>

        <motion.div
          className="border-t hair pt-12 md:pt-16"
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease, delay: 0.1 }}
        >
          <div className="flex items-end justify-between mb-8 md:mb-10">
            <h3 className="display text-[28px] md:text-[36px]" style={{ fontWeight: 400 }}>
              {w.fadeTitle}
            </h3>
            <span className="mono" style={{ color: "var(--faded)" }}>
              {w.illustrative}
            </span>
          </div>

          <div className="slowfade-wrap" style={{ color: "var(--ink)" }} data-testid="slow-fade">
            <SlowFadeGraph labelYour={w.fadeYour} labelNow={w.fadeNow} />
          </div>

          <p
            className="mt-8 md:mt-10 max-w-[64ch] text-[15px] md:text-[17px]"
            style={{ color: "var(--ink)", fontWeight: 300, lineHeight: 1.5 }}
            data-testid="whynow-insight"
          >
            {w.insight}
          </p>
        </motion.div>
      </div>
    </section>
  );
}
